/**
 * useUpdateGroup Hook
 *
 * React Query mutation hook for updating a group.
 */

import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { groupService } from '../../../api/group.service';
import type { GroupSanitized, GroupUpdateDTO } from '../../../types/group.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UpdateGroupVariables {
  groupId: string;
  data: GroupUpdateDTO;
}

/**
 * Hook for updating a group
 */
export const useUpdateGroup = (
  tenantId: string
): UseMutationResult<GroupSanitized, Error, UpdateGroupVariables> => {
  const queryClient = useQueryClient();

  return useMutation<GroupSanitized, Error, UpdateGroupVariables>({
    mutationFn: ({ groupId, data }) => {
      console.log('[useUpdateGroup] Updating group:', groupId);
      return groupService.updateGroup(tenantId, groupId, data);
    },
    onSuccess: (group, variables) => {
      console.log('[useUpdateGroup] Group updated successfully:', group.groupId);
      // Update cached group data
      queryClient.setQueryData(['group', tenantId, variables.groupId], group);
      // Invalidate groups list to refresh the data
      queryClient.invalidateQueries({ queryKey: ['groups', tenantId] });
    },
    onError: (error) => {
      console.error('[useUpdateGroup] Failed to update group:', getErrorMessage(error));
    },
  });
};
